import { Injectable, EventEmitter } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription, Subject } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { HttpHelperService } from './http-helper.service';
import { GeneralUtilsService } from './general-utils.service';

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  progressObserver: Subscription;
  progressEvent: EventEmitter<any> = new EventEmitter();
  progress: number = 0;

  constructor(
    private readonly api: ApiService,
    private readonly httpHelperService: HttpHelperService,
    public auth: AuthService
  ) {}

  /**
   * Extracts File objects from a drag-and-drop DataTransferItemList or FileList
   */
  private extractFilesFromDataTransfer(items: DataTransferItemList | null, filesList?: FileList | null): File[] {
    const files: File[] = [];
    if (items?.length) {
      for (const item of Array.from(items)) {
        if (item.kind === 'file') {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
      return files;
    }
    if (filesList?.length) {
      for (const f of Array.from(filesList)) {
        if (f) files.push(f);
      }
    }
    return files;
  }

  /**
   * Read a File as a data URL
   */
  private readFileAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        const fileReader = new FileReader();
        fileReader.onload = () => resolve(String(fileReader.result || ''));
        fileReader.onerror = (err) => reject(err);
        fileReader.readAsDataURL(file);
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * Attach a file to the provided crop modal and resolve with its data URL
   */
  public setFileOnModal(modal: NgbModalRef, fileOrFiles: File | FileList | File[]): Promise<string> {
    const firstFile: File | undefined = Array.isArray(fileOrFiles)
      ? fileOrFiles[0]
      : fileOrFiles instanceof FileList
        ? fileOrFiles.item(0) || undefined
        : fileOrFiles;
    if (!firstFile) return Promise.reject('No file provided');
    if (modal?.componentInstance) {
      (modal.componentInstance as any).imageFile = firstFile;
    }
    return this.readFileAsDataURL(firstFile);
  }

  public openAvatarModalWithFiles(component: any, files: FileList | File[] | File): void {
    component.openModal();
    this.setFileOnModal(component.modal as NgbModalRef, files).then((dataUrl) => {
      component.currentProfileImage = dataUrl;
      component.setImageOnModal(component, dataUrl);
    });
  }

  /**
   * Convenience helper to extract files directly from a DragEvent
   */
  public extractFilesFromDragEvent(event: DragEvent): File[] {
    return this.extractFilesFromDataTransfer(event.dataTransfer?.items || null, event.dataTransfer?.files || null);
  }

  /**
   * Makes a POST request and updates the progress value
   */
  makeFileRequest(
    url: string,
    params: string[],
    file: File | FileList | File[],
    fileKeyParam?: any,
    bodyParams?: any
  ): Observable<any> {
    return new Observable((observer) => {
      this.progress = 0;
      const formData: FormData = new FormData(),
        xhr: XMLHttpRequest = new XMLHttpRequest();

      if (file instanceof FileList || Array.isArray(file)) {
        for (let i = 0; i < file.length; i++) formData.append(fileKeyParam || 'files', file[i], file[i].name);
      } else {
        formData.append(fileKeyParam || 'file', file, file.name);
      }
      if (bodyParams) {
        if (Object.keys(bodyParams).length) {
          Object.keys(bodyParams).forEach((key) => {
            formData.append(key, bodyParams[key]);
          });
        }
      }

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200 || xhr.status === 201) {
            const res = JSON.parse(xhr.response);
            observer.next(res);
            observer.complete();
          } else {
            observer.error(xhr.response);
          }
        }
      };

      xhr.upload.onprogress = (event) => {
        this.progress = Math.round((event.loaded / event.total) * 100);
        this.progressEvent.emit(this.progress);
        observer.next(this.progress);
      };

      xhr.upload.onerror = (err) => {
        observer.error(this.progress);
      };

      xhr.open('POST', url, true);
      const headers = this.httpHelperService.getHttpHeaders();

      headers.keys().forEach((name) => {
        if (name !== 'Content-Type') {
          const value = headers.get(name);
          if (value) {
            xhr.setRequestHeader(name, value);
          }
        }
      });

      xhr.send(formData);
    });
  }

  /**
   * Convert base64/URLEncoded data component to raw binary data held in a string
   */
  public dataURItoBlob(dataURI: string, _type: string = 'image/jpeg'): Blob {
    const byteString: string = atob(dataURI.split(',')[1]);
    const ab: ArrayBuffer = new ArrayBuffer(byteString.length);
    const ia: Uint8Array = new Uint8Array(ab);

    for (let i: number = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);

    return new Blob([ab], { type: _type });
  }

  /**
   * Upload image file
   */
  public uploadImage(dataURI: string, url?: string): Observable<any> {
    const subject = new Subject<any>();
    try {
      let user;
      if ((user = this.auth.getAuthUser())) {
        const type = dataURI.substring(dataURI.indexOf(':') + 1, dataURI.indexOf(';'));
        const extension = type.substr(type.indexOf('/') + 1);
        const blobImage: any = this.dataURItoBlob(dataURI, type);
        blobImage.lastModifiedDate = new Date();
        blobImage.name = `${Math.floor(Math.random() * 1000000) + 1}.${extension}`;
        const fileImage = <File>blobImage;

        //  /api/users/{userId}/avatar
        const endpoint = url || this.api.baseUrl + '/api/users/' + user.username + '/avatar';
        this.progressObserver = this.makeFileRequest(endpoint, [], fileImage).subscribe(
          (data) => {
            if (typeof data === 'object' && data !== null) {
              subject.next(data);
            }
          },
          (err) => {
            subject.error(null);
          },
          () => {
            subject.complete();
          }
        );
      } else subject.error(null);
    } catch (error) {
      console.log('normal file upload error');
      subject.error(null);
    }

    return subject.asObservable();
  }

  /**
   * Upload multiple files
   *
   * @param files
   * @param url
   */
  public uploadFiles(
    files: File | FileList | any[],
    url,
    externalUrl?,
    keyParam?: any,
    bodyParams?: any
  ): Observable<any> {
    return this.makeFileRequest(externalUrl ? url : this.api.baseUrl + url, [], files, keyParam, bodyParams);
  }
}
